<template>
  <div :class="['message-item', message.role, message.type, { full: fullScreenMode }]">
    <div class="message-wrapper">
      <div v-if="isAi" class="message-profile">
        <i v-if="isLoading || isAnswering" class="icon icon--becle-entering"></i>
        <i v-else-if="this.message.error" class="icon icon--becle-crying"></i>
        <template v-else-if="ratingLoading">
          <i v-if="rating === RATING_LIST.GOOD" class="icon icon--becle-good"></i>
          <i v-else-if="rating === RATING_LIST.BAD" class="icon icon--becle-bad"></i>
        </template>
        <i v-else class="icon icon--becle-profile"></i>
      </div>
      <div v-else class="message-profile">{{ userData.userId[0] }}</div>
      <div class="message-text-container">
        <template v-if="!this.message.error">
          <span v-if="isLoading && isAi" class="loading-text">{{ $t('mine.chat.loading') }}</span>
          <div
            class="chat-message-text"
            v-html="convertMarkdownToHtml(message.message)"
            linkTarget="_blank"
          ></div>
          <div class="message-buttons" v-if="isAi && !isLoading && !isAnswering && message.type !== 'summary'">
            <div class="rating">
              <span>{{ $t('mine.chat.rating') }}</span>
              <div class="rating-buttons">
                <button :class="['rating-button', {good: rating === RATING_LIST.GOOD}]" @click="rateAnswer(RATING_LIST.GOOD)">
                  <i class="icon icon--rating-good"></i>
                </button>
                <button :class="['rating-button', {bad: rating === RATING_LIST.BAD}]" @click="rateAnswer(RATING_LIST.BAD)">
                  <i class="icon icon--rating-bad"></i>
                </button>
              </div>
            </div>
            <button class="copy">
              <i class="icon icon--copy-long" @click="copy"></i>
            </button>
          </div>
        </template>
        <p v-else class="bold" v-html="$t(`mine.chat.error.${message.error}`)" ></p>
        <i v-if="message.role === 'human'" class="icon icon--chat-arrow"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { rateAnswer } from '@/api/ai'
import { convertMarkdownToHtml } from '@/libs/utils'

export default {
  name: 'MessageItem',
  props: {
    message: Object,
    isLoading: Boolean,
    isAnswering: Boolean,
    fullScreenMode: Boolean
  },
  data () {
    return {
      RATING_LIST: {
        'GOOD': 5,
        'BAD': 1
      },
      rating: null,
      ratingLoading: false
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    isAi () {
      return this.message.role === 'ai'
    }
  },
  methods: {
    convertMarkdownToHtml,
    copy () {
      navigator.clipboard.writeText(this.message.message)
        .then(() => this.$emit('toast', this.$t('mine.chat.copy')))
    },
    rateAnswer (rating) {
      const newRating = this.rating === rating ? null : rating
      rateAnswer({ id: this.message.id, rating: newRating })
        .then(() => {
          this.rating = newRating
          this.ratingLoading = Boolean(newRating)
          setTimeout(() => {
            this.ratingLoading = false
          }, 2000)
        }).catch((e) => {
          console.log(e)
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.message-item {
  width: 100%;
  max-width: 1317px;
  padding: 20px 120px;
  box-sizing: border-box;
  margin: 0 auto;

  .message-wrapper {
    display: flex;
    .message-profile {
      flex-shrink: 0;
    }

    .message-text-container {
      position: relative;
      font-size: 18px;
      line-height: 1.5;
      font-weight: 400;
      .loading-text {
        &:after {
          padding-left: 2px;
          content: '';
          animation: dots 2.5s steps(3, end) infinite;
        }
        @keyframes dots {
          0%   { content: ''; }
          25%  { content: '.'; }
          50%  { content: '..'; }
          75%  { content: '...'; }
          100% { content: ''; }
        }
      }
      .chat-message-text {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-height: 24px;
        color: #262626;
        a,
        :-webkit-any-link {
          white-space: normal;
          word-break: break-word;
          text-decoration: underline;
        }
        ul {
          list-style: disc;
        }
      }
      .bold {
        font-weight: 600;
      }
    }
  }

  &.human {
    .message-wrapper {
      align-items: flex-start;
      flex-direction: row-reverse;
      gap: 23px;

      .message-profile {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        padding: 4px;
        background-color: #F7A300;
        box-sizing: border-box;
        border-radius: 5px;
        color: #FFF;
        font-size: 22px;
        font-weight: 600;
        text-transform: uppercase;
      }
      .message-text-container {
        padding: 20px 24px;
        margin: 18px 0 0 60px;
        background-color: #FFF;
        border-radius: 16px;
        .icon--chat-arrow {
          position: absolute;
          top: -2px;
          right: -17px;
        }
      }
    }
  }

  &.ai {
    .message-wrapper {
      gap: 14px;
      .message-profile {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background-color: #BAC5FF;
        overflow: hidden;
      }
      .message-text-container {
        margin: 9px 60px 0 0;
        .message-buttons {
          @include flexYcenter;
          gap: 12px;
          margin-top: 30px;
          .rating {
            @include flexYcenter;
            padding: 9px 12px 9px 11px;
            gap: 12px;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            background: #FFF;
            font-size: 16px;
            .rating-buttons {
              @include flexYcenter;
              gap: 4px;
              .rating-button {
                display: flex;
                border-radius: 50px;
                opacity: 0.38;
                &.good {
                  opacity: 1;
                  background-color: #FDF26F;
                }
                &.bad {
                  opacity: 1;
                  background-color: #FF6B35;
                }
              }
            }
          }
          .copy {
            display: flex;
            &:hover {
              border-radius: 4px;
              background-color: #D4D4D4;
            }
          }
        }
      }
    }
  }

  &.full {
    padding: 20px 40px;
    max-width: 1157px;
    &.ai {
      .message-wrapper {
        .message-text-container {
          margin-right: 40px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .message-item {
    padding: 20px 40px;
    .message-text-container {
      padding: 6px 0 6px 16px;
    }
    &.ai {
      .message-wrapper {
        .message-text-container {
          margin-right: 40px;
        }
      }
    }
  }
}
</style>
